import { Button, Image, Upload, UploadProps } from "antd";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import { useEffect, useState } from "react";
import CloseIcon from "../../../../assets/icons/CloseIcon";
import LinkIcon from "../../../../assets/images/external-link.png";
import {
  default as PDF_Icon,
  default as PDF_IMG,
} from "../../../../assets/images/pdf.svg";
import { useUploadPhotoInEmployeeMutation } from "../../../../features/emp-setting/employee/employeeApi";
import useError from "../../../../hooks/useError";
import { toast } from "../../../../lib/toast";
import { stringToArray } from "../../../../utils/array";
import { getExtension } from "../../../../utils/string";

type Props = {
  errorMessage?: string;
  form: any;
  name: string;
  disabled?: boolean;
  fileType?: boolean;
  accept?: string;
  onChange?: (url: string | null) => void;
  listFile?: boolean;
  size?: "large" | "middle" | "small";
  fileSize?: number;
  showImage?: boolean;
} & Omit<UploadProps, "name">;

function accessFile(accept: string) {
  return accept?.toLowerCase().includes("png") ||
    accept?.toLowerCase().includes("jpg") ||
    accept?.toLowerCase().includes("jpeg") ||
    accept?.toLowerCase().includes("svg") ||
    accept?.toLowerCase().includes("gif")
    ? true
    : false;
}

export default function EmployeeFileUpload({
  errorMessage = "File upload fail",
  form,
  name,
  disabled,
  onChange,
  accept = ".png,.jpeg",
  listFile = true,
  fileType = true,
  size = "large",
  fileSize,
  showImage,
}: Props) {
  const [fileName, setFileName] = useState("No file chosen");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [
    uploadPhotoInEmployeeData,
    { isLoading, isError, error, data, isSuccess },
  ] = useUploadPhotoInEmployeeMutation();
  useError(isError, error, errorMessage);

  const handleUpload = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);
    const isImage = accessFile(getExtension(file?.name));

    const currentFileSize = fileSize || (isImage ? 5 : 10);

    const MAX_FILE_SIZE = currentFileSize * 1024 * 1024; // MB in bytes

    if (file?.size > MAX_FILE_SIZE) {
      toast(
        "error",
        `The file size exceeds the ${currentFileSize}MB limit. Please upload a smaller file`
      );
      return false;
    }

    uploadPhotoInEmployeeData(formData);
    return false;
  };

  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    setFileList(info.fileList);

    if (info.file.status === "removed") {
      onChange && onChange(null);
      setFileName("No file chosen");
    }
  };

  useEffect(() => {
    if (isSuccess) {
      setFileName(data?.Location);
      onChange && onChange(data?.Location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (fileType) {
      const timer = setTimeout(() => {
        const file = form.getFieldValue(
          true ? stringToArray(name, ".") : [name]
        );

        if (file) {
          onChange && onChange(file);
          setFileName(file);
        }
      }, 1000);

      const file = form.getFieldValue(
        listFile ? stringToArray(name, ".") : [name]
      );

      if (file) {
        onChange && onChange(file);
        setFileName(file);
      }

      return () => {
        clearTimeout(timer);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, name, fileType, listFile]);

  const fileNameField = fileName?.split("/")?.pop();

  const showFile =
    fileName === "No file chosen" || !fileName
      ? null
      : getExtension(fileName) === "jpg" ||
        getExtension(fileName) === "png" ||
        getExtension(fileName) === "jpeg"
      ? fileName
      : PDF_Icon;

  const isCloseIcon = fileNameField && fileNameField !== "No file chosen";

  return (
    <div>
      <div className="relative">
        <Upload
          name="file"
          beforeUpload={handleUpload}
          maxCount={1}
          accept={accept}
          showUploadList={false}
          onChange={handleChange}
          fileList={fileList}
          disabled={disabled || isLoading}
        >
          <Button
            className={`w-full flex items-center !justify-start !p-0 !border-display-medium rounded-md relative disabled:!bg-[#f1f1f1] ${
              size === "large" ? "!h-[46px]" : ""
            }`}
            block
            size={size}
            disabled={disabled}
            htmlType="button"
          >
            <span className="!flex justify-center items-center px-3 bg-[#5e718d] text-white h-full rounded-s">
              Browse file
            </span>
            <div className="px-2.5 gap-2 text-sm font-medium text-extra-gray overflow-hidden whitespace-nowrap text-ellipsis">
              {isLoading ? (
                "Uploading..."
              ) : (
                <span
                  className="overflow-hidden text-ellipsis"
                  style={{
                    direction: "unset",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {(fileNameField && fileNameField?.length > 30
                    ? `${fileNameField?.slice(0, 30)}...`
                    : fileNameField) || "No file chosen"}
                </span>
              )}
            </div>
          </Button>
        </Upload>
        {isCloseIcon && !disabled ? (
          <span
            className="absolute right-3 top-2/4 -translate-y-2/4 text-body-text cursor-pointer size-4 flex items-center justify-center"
            onClick={() => {
              onChange && onChange(null);
              setFileName("");
            }}
          >
            <CloseIcon width={8} height={8} />
          </span>
        ) : null}
      </div>
      {showImage && showFile ? (
        <div className="mt-1.5 flex items-end gap-2">
          <Image
            src={getExtension(fileName) === "pdf" ? PDF_IMG : fileName}
            width={getExtension(fileName) === "pdf" ? 100 : 200}
            height={getExtension(fileName) === "pdf" ? 150 : 100}
          />
          <span className="text-accent flex items-center gap-1 text-xs mt-1">
            <a
              href={fileName}
              target="_blank"
              className="-mt-[2px] cursor-pointer"
              rel="noreferrer"
              download="Attachment"
            >
              <img className="size-3" src={LinkIcon} alt="" />
            </a>
          </span>
        </div>
      ) : null}
    </div>
  );
}
